import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Typography,
  message
} from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { invalidateRuntimeSettings } from "@core/runtime-settings";
import { fetchSprTov } from "@components/health/clients/health-clients-api";
import { pick, toNumber } from "@components/health/clients/health-clients-utils";
import { fetchSprTov as fetchSprTovItem } from "../../sprs/tov/spr-tov-api";
import { fetchSettings, fetchSettingsLookups, updateSettings } from "./settings-api";
import {
  initializeWorkplaceSettings,
  loadWorkplaceSettings,
  resetTableAppearance,
  saveWorkplaceSettings
} from "./settings-workplace";
import "./settings-page.css";

const { Text } = Typography;

const SYSTEM_FIELDS = [
  "dateBlock",
  "vidBlockExp",
  "notShowFood3",
  "vidPutDefault",
  "vidPitDefault",
  "vidLechDefault",
  "bronAutoZoom",
  "bronShowTime",
  "autobronDays",
  "vidAutonumber",
  "rezervFillFullDays",
  "autoNumberDocPack",
  "termIdTovKarta",
  "bronSort",
  "priemVrach",
  "cntLechMest",
  "lockExpAfterLeave",
  "rentLockPlat",
  "uslSynhro",
  "fillScheduleDays",
  "platOsnNecessary",
  "vrachNotNecessary",
  "cardLinkVid",
  "vidPitFirstDay",
  "phoneVid",
  "docKassaAutonumber",
  "platUslNeed",
  "minDepos",
  "useGuarantee",
  "extraPms",
  "predAutosearch",
  "skdAccBefore",
  "skdAccAfter",
  "predMail",
  "onlineSrokMin",
  "predUniqPhone",
  "sklRestControl"
];

const REQUISITES_FIELDS = [
  "companyName",
  "companyAddress",
  "companyRegion",
  "companyRegionId",
  "companyPostalCode",
  "companyId",
  "companyDepId",
  "companyOgrn",
  "companyOkpo",
  "companyOkato",
  "companyGmt",
  "companyLicenseNumber",
  "companyLicenseOrg",
  "egiszGuid",
  "egiszLpu",
  "companySignId",
  "egiszMode"
];

const WORKPLACE_FIELDS = [
  "tableEvenColor",
  "tableOddColor",
  "tableSelectedColor",
  "tableFontName",
  "tableFontSize",
  "idTerm",
  "idSkl",
  "idBranch"
];

const EMPTY_LOOKUPS = {
  vidPut: [],
  vidPit: [],
  vidLech: [],
  tov: [],
  terms: [],
  warehouses: [],
  branches: []
};

const integerProps = { min: 0, precision: 0, className: "settings-page__number" };

const lookupOptions = (items) => {
  const options = (Array.isArray(items) ? items : []).map(({ id, name }) => ({
    value: id,
    label: name
  }));
  return options.some(({ value }) => value === 0)
    ? options
    : [{ value: 0, label: "Не выбрано" }, ...options];
};

const selectLookup = (items) => (
  <Select
    showSearch
    optionFilterProp="label"
    options={lookupOptions(items)}
    placeholder="Не выбрано"
  />
);

const selectField = (name, label, options) => (
  <Form.Item name={name} label={label}>
    <Select options={options} />
  </Form.Item>
);

const boolField = (name, label) => (
  <Form.Item name={name} valuePropName="checked" className="settings-page__check">
    <Checkbox>{label}</Checkbox>
  </Form.Item>
);

const numberField = (name, label, props = {}) => (
  <Form.Item name={name} label={label}>
    <InputNumber {...integerProps} {...props} />
  </Form.Item>
);

const pickFields = (values, fields) =>
  fields.reduce((result, field) => {
    result[field] = values[field];
    return result;
  }, {});

const parseDate = (value) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const formatDate = (value) => (value && dayjs.isDayjs(value) ? value.format("YYYY-MM-DD") : null);

const flattenSettings = (value) => ({
  ...(value?.system || {}),
  ...(value?.requisites || {})
});

const normalizeTovOptions = (data) =>
  (Array.isArray(data) ? data : [])
    .map((row) => {
      const name = pick(row, "name", "Name") ?? "";
      const art = pick(row, "art", "Art");
      return {
        value: toNumber(pick(row, "idTov", "IdTov")),
        label: art ? `${name} (${art})` : name
      };
    })
    .filter((row) => row.value != null);

const normalizeLookups = (value) => ({
  vidPut: value?.voucherTypes || [],
  vidPit: value?.mealTypes || [],
  vidLech: value?.treatmentTypes || [],
  tov: value?.goodsServices || [],
  terms: value?.quickMenus || [],
  warehouses: value?.warehouses || [],
  branches: value?.branches || []
});

const SettingsPage = () => {
  const [form] = Form.useForm();
  const law = useSelector((state) => state.user.session?.law);
  const canOpen = !law || law.length <= 40 || Number(law[40]) === 1;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(null);
  const [lookups, setLookups] = useState(EMPTY_LOOKUPS);
  const [reloadKey, setReloadKey] = useState(0);
  const [activeSection, setActiveSection] = useState("system");
  const [initialWorkplace] = useState(() => initializeWorkplaceSettings());
  const [tovOptions, setTovOptions] = useState([]);
  const [tovLoading, setTovLoading] = useState(false);
  const tovTimerRef = useRef(0);
  const tovSearchRef = useRef(0);

  const loadTov = useCallback((q) => {
    clearTimeout(tovTimerRef.current);
    tovTimerRef.current = setTimeout(() => {
      const requestId = ++tovSearchRef.current;
      setTovLoading(true);
      fetchSprTov(q)
        .then((data) => {
          if (requestId !== tovSearchRef.current) return;
          const next = normalizeTovOptions(data);
          const selectedId = toNumber(form.getFieldValue("priemVrach"));
          setTovOptions((previous) => {
            const selected = previous.find((item) => item.value === selectedId);
            return selected && !next.some((item) => item.value === selectedId) ? [selected, ...next] : next;
          });
        })
        .catch(() => {
          if (requestId === tovSearchRef.current) setTovOptions([]);
        })
        .finally(() => {
          if (requestId === tovSearchRef.current) setTovLoading(false);
        });
    }, 300);
  }, [form]);

  useEffect(() => () => clearTimeout(tovTimerRef.current), []);

  useEffect(() => {
    if (!canOpen) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError("");

    Promise.all([fetchSettings(controller.signal), fetchSettingsLookups(controller.signal)])
      .then(([serverSettings, serverLookups]) => {
        const nextSettings = serverSettings || { system: {}, requisites: {} };
        const flatSettings = flattenSettings(nextSettings);
        setSettings(nextSettings);
        setLookups({ ...EMPTY_LOOKUPS, ...normalizeLookups(serverLookups) });
        form.setFieldsValue({
          ...flatSettings,
          dateBlock: parseDate(flatSettings.dateBlock),
          ...(reloadKey === 0 ? initialWorkplace : loadWorkplaceSettings())
        });

        const priemVrach = toNumber(flatSettings.priemVrach);
        if (priemVrach) {
          fetchSprTovItem(priemVrach)
            .then((row) => {
              if (controller.signal.aborted) return;
              const [selected] = normalizeTovOptions([row]);
              if (!selected) return;
              setTovOptions((previous) =>
                previous.some((item) => item.value === selected.value) ? previous : [selected, ...previous]
              );
            })
            .catch(() => undefined);
        }
        loadTov("");
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") {
          setError(requestError.message || "Не удалось загрузить настройки");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [canOpen, form, initialWorkplace, loadTov, reloadKey]);

  const save = useCallback(async () => {
    if (!canOpen) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError("");

      const system = pickFields(values, SYSTEM_FIELDS);
      system.dateBlock = formatDate(values.dateBlock);
      system.priemVrach = values.priemVrach ?? 0;
      const requisites = pickFields(values, REQUISITES_FIELDS);
      const payload = {
        ...(settings || {}),
        system: { ...(settings?.system || {}), ...system },
        requisites: { ...(settings?.requisites || {}), ...requisites }
      };
      const saved = await updateSettings(payload);
      setSettings(saved || payload);
      invalidateRuntimeSettings();
      saveWorkplaceSettings(pickFields(values, WORKPLACE_FIELDS));
      message.success("Настройки сохранены");
    } catch (saveError) {
      if (saveError?.errorFields) return;
      setError(saveError.message || "Не удалось сохранить настройки");
    } finally {
      setSaving(false);
    }
  }, [canOpen, form, settings]);

  const handleValuesChange = useCallback(
    (changedValues, allValues) => {
      if (Object.keys(changedValues).some((key) => WORKPLACE_FIELDS.includes(key))) {
        saveWorkplaceSettings(pickFields(allValues, WORKPLACE_FIELDS));
      }
    },
    []
  );

  const resetAppearance = useCallback(() => {
    const current = pickFields(form.getFieldsValue(), WORKPLACE_FIELDS);
    const reset = resetTableAppearance(current);
    form.setFieldsValue(pickFields(reset, WORKPLACE_FIELDS));
    message.success("Оформление таблиц сброшено");
  }, [form]);

  const systemTab = useMemo(
    () => (
      <div className="settings-page__sections">
        <section>
          <h2>Бронирование и путёвки</h2>
          <div className="settings-page__grid">
            <Form.Item name="dateBlock" label="Дата запрета редактирования">
              <DatePicker format="DD.MM.YYYY" allowClear />
            </Form.Item>
            {selectField("vidBlockExp", "Вид блокировки путёвок", [
              { value: 0, label: "По дате документа" },
              { value: 1, label: "По дате заезда" },
              { value: 2, label: "По дате выезда" }
            ])}
            <Form.Item name="vidPutDefault" label="Вид путёвки по умолчанию">
              {selectLookup(lookups.vidPut)}
            </Form.Item>
            <Form.Item name="vidPitDefault" label="Вид питания по умолчанию">
              {selectLookup(lookups.vidPit)}
            </Form.Item>
            <Form.Item name="vidLechDefault" label="Вид лечения по умолчанию">
              {selectLookup(lookups.vidLech)}
            </Form.Item>
            {numberField("autobronDays", 'Срок расчёта даты "Бронировать до", дней')}
            {selectField("vidAutonumber", "Автонумерация путёвок", [
              { value: 0, label: "Не использовать" },
              { value: 1, label: "По году" },
              { value: 2, label: "По всей базе" }
            ])}
            <Form.Item name="termIdTovKarta" label="Залоговая стоимость карты">
              {selectLookup(lookups.tov)}
            </Form.Item>
            {selectField("bronSort", "Сортировка номеров в брони", [
              { value: 0, label: "По наименованию" },
              { value: 1, label: "По корпусам" },
              { value: 2, label: "По категории" }
            ])}
            <Form.Item name="priemVrach" label="Услуга приёма врача для регистрации">
              <Select
                showSearch
                allowClear
                filterOption={false}
                loading={tovLoading}
                options={tovOptions}
                placeholder="Не выбрано"
                notFoundContent={tovLoading ? <Spin size="small" /> : "Ничего не найдено"}
                onSearch={loadTov}
                onClear={() => loadTov("")}
              />
            </Form.Item>
            {numberField("cntLechMest", "Ограничение мест на лечение")}
            {numberField("fillScheduleDays", "Напоминать о незаполненных графиках, дней")}
          </div>
          <div className="settings-page__checks">
            {boolField("notShowFood3", "Не показывать «полдник» при продаже")}
            {boolField("bronAutoZoom", "Автомасштабирование брони")}
            {boolField("bronShowTime", "Показывать время в брони")}
            {boolField("rezervFillFullDays", "Заполнять резерв полными днями")}
            {boolField("autoNumberDocPack", "Автонумерация пакетов документов")}
            {boolField("lockExpAfterLeave", "Блокировать расходы после выезда")}
            {boolField("rentLockPlat", "Блокировать оплату объектов аренды")}
            {boolField("uslSynhro", "Синхронизация услуг")}
            {boolField("platOsnNecessary", "Обязательно основание платежа")}
            {boolField("vrachNotNecessary", "Врач не обязателен")}
          </div>
        </section>

        <section>
          <h2>Карты, касса и интеграции</h2>
          <div className="settings-page__grid">
            {selectField("cardLinkVid", "Привязка карт", [
              { value: 0, label: "К клиентам лечебной части" },
              { value: 1, label: "К клиентам путёвки" }
            ])}
            {selectField("vidPitFirstDay", "Питание в день заселения", [
              { value: 0, label: "Не выбрано" },
              { value: 1, label: "Завтрак" },
              { value: 2, label: "Обед" },
              { value: 3, label: "Полдник" },
              { value: 4, label: "Ужин" }
            ])}
            {selectField("phoneVid", "Формат номера телефона", [
              { value: 0, label: "Не контролировать" },
              { value: 1, label: "+7XXXXXXXXXX" }
            ])}
            {selectField("docKassaAutonumber", "Нумерация кассовых документов", [
              { value: 0, label: "Нет" },
              { value: 1, label: "По всей базе" },
              { value: 2, label: "По году" },
              { value: 3, label: "По году и кассе" },
              { value: 4, label: "По году, кассе и приходу/расходу" }
            ])}
            {numberField("minDepos", "Минимальный остаток депозита")}
            {numberField("skdAccBefore", "СКД: запас до начала, минут")}
            {numberField("skdAccAfter", "СКД: запас после окончания, минут")}
            <Form.Item name="predMail" label="Почта клиента по умолчанию">
              <Input type="email" />
            </Form.Item>
            {numberField("onlineSrokMin", "Минимальный срок ссылки, минут")}
          </div>
          <div className="settings-page__checks">
            {boolField("platUslNeed", "Обязательна услуга в платеже")}
            {boolField("useGuarantee", "Оплачивать залог при выдаче карты")}
            {boolField("extraPms", "Дополнительная интеграция PMS")}
            {boolField("predAutosearch", "Автоматический поиск контрагентов")}
            {boolField("predUniqPhone", "Уникальный телефон клиента")}
          </div>
        </section>

        <section>
          <h2>Склад</h2>
          <div className="settings-page__grid">
            {selectField("sklRestControl", "Контроль остатков при расходе со склада", [
              { value: 0, label: "Не контролировать" },
              { value: 1, label: "Предупреждать" },
              { value: 2, label: "Запрещать" }
            ])}
          </div>
        </section>
      </div>
    ),
    [lookups, tovOptions, tovLoading, loadTov]
  );

  const workplaceTab = (
    <div className="settings-page__sections">
      <section>
        <div className="settings-page__section-heading">
          <div>
            <h2>Оформление таблиц</h2>
            <Text type="secondary">Изменения применяются сразу и хранятся в этом браузере.</Text>
          </div>
          <Button onClick={resetAppearance}>Сбросить оформление</Button>
        </div>
        <div className="settings-page__grid settings-page__grid--appearance">
          <Form.Item name="tableEvenColor" label="Чётные строки">
            <Input type="color" />
          </Form.Item>
          <Form.Item name="tableOddColor" label="Нечётные строки">
            <Input type="color" />
          </Form.Item>
          <Form.Item name="tableSelectedColor" label="Выбранная строка">
            <Input type="color" />
          </Form.Item>
          <Form.Item name="tableFontName" label="Шрифт таблиц">
            <Input placeholder="Системный шрифт" />
          </Form.Item>
          {numberField("tableFontSize", "Размер шрифта, px", {
            min: 0,
            max: 72
          })}
        </div>
      </section>
      <section>
        <h2>Рабочее место</h2>
        <div className="settings-page__grid">
          <Form.Item name="idTerm" label="Терминал">
            {selectLookup(lookups.terms)}
          </Form.Item>
          <Form.Item name="idSkl" label="Склад">
            {selectLookup(lookups.warehouses)}
          </Form.Item>
          <Form.Item name="idBranch" label="Филиал">
            {selectLookup(lookups.branches)}
          </Form.Item>
        </div>
      </section>
    </div>
  );

  const requisitesTab = (
    <div className="settings-page__sections">
      <section>
        <h2>Организация</h2>
        <div className="settings-page__grid">
          <Form.Item name="companyName" label="Наименование организации">
            <Input />
          </Form.Item>
          <Form.Item name="companyAddress" label="Адрес">
            <Input />
          </Form.Item>
          <Form.Item name="companyRegion" label="Регион">
            <Input />
          </Form.Item>
          <Form.Item name="companyRegionId" label="Код региона">
            <Input />
          </Form.Item>
          <Form.Item name="companyPostalCode" label="Почтовый индекс">
            <Input />
          </Form.Item>
          <Form.Item name="companyId" label="Идентификатор организации">
            <Input />
          </Form.Item>
          <Form.Item name="companyDepId" label="Идентификатор подразделения">
            <Input />
          </Form.Item>
          <Form.Item name="companyOgrn" label="ОГРН">
            <Input />
          </Form.Item>
          <Form.Item name="companyOkpo" label="ОКПО">
            <Input />
          </Form.Item>
          <Form.Item name="companyOkato" label="ОКАТО">
            <Input />
          </Form.Item>
          {numberField("companyGmt", "Часовой пояс GMT", { min: -12, max: 14 })}
          <Form.Item name="companyLicenseNumber" label="Номер лицензии">
            <Input />
          </Form.Item>
          <Form.Item name="companyLicenseOrg" label="Орган, выдавший лицензию">
            <Input />
          </Form.Item>
        </div>
      </section>
      <section>
        <h2>ЕГИСЗ</h2>
        <div className="settings-page__grid">
          <Form.Item name="egiszGuid" label="Авторизационный токен">
            <Input />
          </Form.Item>
          <Form.Item name="egiszLpu" label="Идентификатор МО">
            <Input />
          </Form.Item>
          <Form.Item name="companySignId" label="ID электронной подписи">
            <Input />
          </Form.Item>
          {selectField("egiszMode", "Режим ЕГИСЗ", [
            { value: 0, label: "Демонстрационный" },
            { value: 1, label: "Рабочий" }
          ])}
        </div>
      </section>
    </div>
  );

  if (!canOpen) {
    return (
      <div className="settings-page">
        <Alert type="error" showIcon message="Нет права 40 на редактирование настроек" />
      </div>
    );
  }

  return (
    <div className="settings-page">
      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError("")}
          className="settings-page__alert"
        />
      ) : null}

      {loading ? (
        <div className="settings-page__loading">
          <Spin size="large" tip="Загрузка настроек..." />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          disabled={saving}
          onValuesChange={handleValuesChange}
          className="settings-page__form"
        >
          <div className="settings-page__section-toolbar">
            <Select
              className="settings-page__section-select"
              value={activeSection}
              onChange={setActiveSection}
              options={[
                { value: "system", label: "Система" },
                { value: "workplace", label: "Рабочее место" },
                { value: "requisites", label: "Реквизиты" }
              ]}
            />
            <Space>
              <Button onClick={() => setReloadKey((key) => key + 1)} disabled={saving}>
                Обновить
              </Button>
              <Button type="primary" onClick={save} loading={saving} disabled={!settings}>
                Сохранить
              </Button>
            </Space>
          </div>
          <div className="settings-page__content">
            {activeSection === "system" && systemTab}
            {activeSection === "workplace" && workplaceTab}
            {activeSection === "requisites" && requisitesTab}
          </div>
        </Form>
      )}
    </div>
  );
};

export default SettingsPage;
